import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SectionContainer from "./SectionContainer";
import { statItems } from "../../data/landingData";
import { useCountUp, useInView } from "../../hooks/useCountUp";

const StatCard: React.FC<{
  id: string;
  index: number;
}> = ({ id, index }) => {
  const { t } = useTranslation("home");
  const { ref, inView } = useInView(0.3);
  const item = statItems[index];

  const count = useCountUp({
    end: item.value,
    decimals: item.decimals ?? 0,
    enabled: inView,
  });

  const emoji = t(`stat_${id}_emoji`);
  const prefix = t(`stat_${id}_prefix`, { defaultValue: "" });
  const suffix = t(`stat_${id}_suffix`);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="text-center p-6 rounded-2xl bg-space-light/50 border border-white/5"
    >
      <div className="text-3xl mb-2">{emoji}</div>
      <div className="text-3xl md:text-4xl font-extrabold text-gradient-brand mb-2 tabular-nums">
        {prefix}
        {count}
        {suffix}
      </div>
      <p className="text-body-sm text-gray-400">{t(`stat_${id}_label`)}</p>
    </motion.div>
  );
};

const StatsSection: React.FC = () => {
  const { t } = useTranslation("home");

  return (
    <SectionContainer fullWidthBg bgClassName="bg-space-light/20">
      <motion.h2
        className="text-h2 text-center mb-12 text-white"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        {t("stats_title")}
      </motion.h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {statItems.map((item, index) => (
          <StatCard key={item.id} id={item.id} index={index} />
        ))}
      </div>
    </SectionContainer>
  );
};

export default StatsSection;
